<script lang="ts">
    import { untrack } from 'svelte';
    import { fade } from 'svelte/transition';
    import type { TileImage } from '@shared/types/types';
    import { getImageUrl } from '@tile/components/product-image/ImageUrlBuilder.ts';
    import type { ImageAspectRatio, ImageLoadingOptions } from '@tile/components/product-image/types.ts';

    interface Props {
        image: TileImage;
        imageLoadingOptions: ImageLoadingOptions;
        variationId: string;
        size?: { width: string; height: string };
        aspectRatio?: ImageAspectRatio;
        shouldDim?: boolean;
    }

    let { image, variationId, imageLoadingOptions, size, aspectRatio, shouldDim }: Props = $props();

    const imagePaddingTop = $derived.by(() => {
        if (!aspectRatio) return '100%';

        switch (aspectRatio) {
            case 'landscape':
            case 'landscape-two-column':
                return '69.5%';
            case 'portrait':
                return '143%';
            default:
                return '100%';
        }
    });

    let loadingError = $state(false);

    const webpSrc = $derived(getImageUrl(image.id ?? '', 'webp', aspectRatio, shouldDim));
    const jpegSrc = $derived(getImageUrl(image.id ?? '', 'jpeg', aspectRatio, shouldDim));
    const imgSrc = $derived(loadingError ? (image.fallback ?? '') : jpegSrc);

    /**
 *
 *
 */
    let lastVariationId = $state(untrack(() => variationId));
    $effect(() => {
        if (variationId !== lastVariationId) {
            loadingError = false;
            lastVariationId = variationId;
        }
    });
</script>

<div
    class="reptile-product-image"
    style:width={size?.width}
    style:height={size?.height}
    style:padding-top={imagePaddingTop}
>
    <div class="reptile-product-image__frame" data-qa={`ftfind-product-image-${variationId}`}>
        {#key variationId}
            <picture
                class="reptile-product-image__picture"
                class:reptile-product-image__picture--dim={shouldDim}
                in:fade
            >
                {#if !loadingError}
                    <source srcset={webpSrc} type="image/webp" />
                    <source srcset={jpegSrc} type="image/jpeg" />
                {/if}
                <img
                    class="reptile-product-image__picture"
                    src={imgSrc}
                    alt={image.alt ?? ''}
                    onerror={() => {
                        loadingError = true;
                    }}
                    loading={imageLoadingOptions.loading}
                    fetchpriority={imageLoadingOptions.fetchpriority}
                />
            </picture>
        {/key}
    </div>
</div>

<style lang="scss">
    @use '@otto-ec/design-tokens/tokens';

    .reptile-product-image {
        position: relative;

        &__frame {
            position: absolute;
            display: block;
            inset: 0;
            width: 100%;
            background-color: tokens.$oc-base-color-white-100;
            padding: tokens.$oc-semantic-spacing-12 tokens.$oc-semantic-spacing-12 0;
            user-select: none;
        }

        &__picture {
            display: block;
            height: 100%;
            width: 100%;
            object-fit: contain;
            object-position: center;

            &--dim {
                /*                                                                                         */
                /*                                                                     */
                /*                                                                   */
                background-color: #f5f5f5;
            }
        }
    }
</style>
